/**
 * Sign-in and the operator gate. The gate is a courtesy, not the boundary: the
 * real enforcement is the `operator` custom claim (set by the `beforeSignIn`
 * blocking function) that the Firestore/Storage rules require. A user without the
 * claim can sign in to Google but sees the "not an operator" page and can read
 * and write nothing.
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
    GoogleAuthProvider,
    signInWithPopup,
    signOut,
    onAuthStateChanged,
    type User,
} from 'firebase/auth';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { auth } from './firebase';
import { BirdLogo } from './components/BirdLogo';

export interface Operator {
    uid: string;
    email: string;
    displayName: string;
}

const AuthContext = createContext<Operator | undefined>(undefined);

/** The signed-in operator; only defined inside `AuthGate`'s children. */
export function useOperator(): Operator {
    const op = useContext(AuthContext);
    if (!op) throw new Error('useOperator used outside an authenticated tree');
    return op;
}

export function signOutOperator(): Promise<void> {
    return signOut(auth);
}

type AuthState =
    | { phase: 'loading' }
    | { phase: 'signed-out' }
    | { phase: 'not-operator'; email: string }
    | { phase: 'operator'; operator: Operator };

export function AuthGate({ children }: { children: ReactNode }) {
    const [state, setState] = useState<AuthState>({ phase: 'loading' });

    useEffect(() => {
        return onAuthStateChanged(auth, async (user: User | null) => {
            if (!user) return setState({ phase: 'signed-out' });
            // Force-refresh so a just-set claim (or a just-removed one) is seen.
            const token = await user.getIdTokenResult(true);
            if (token.claims.operator === true) {
                setState({
                    phase: 'operator',
                    operator: { uid: user.uid, email: user.email ?? '', displayName: user.displayName ?? user.email ?? '' },
                });
            } else {
                setState({ phase: 'not-operator', email: user.email ?? '' });
            }
        });
    }, []);

    if (state.phase === 'loading') {
        return (
            <Centered>
                <CircularProgress />
            </Centered>
        );
    }

    if (state.phase === 'signed-out') {
        return (
            <Centered>
                <Stack spacing={2} alignItems="center">
                    <BirdLogo size={96} />
                    <Button variant="contained" onClick={() => void signInWithPopup(auth, new GoogleAuthProvider())}>
                        Sign in with Google
                    </Button>
                </Stack>
            </Centered>
        );
    }

    if (state.phase === 'not-operator') {
        return (
            <Centered>
                <Stack spacing={2} alignItems="center" maxWidth={520} textAlign="center">
                    <Typography variant="h5">Not an operator</Typography>
                    <Typography color="text.secondary">
                        {state.email} is signed in but is not a release operator. Ask a release admin to add you to{' '}
                        <code>automation/deployer/functions/operators.json</code>.
                    </Typography>
                    <Button onClick={() => void signOutOperator()}>Sign out</Button>
                </Stack>
            </Centered>
        );
    }

    return <AuthContext.Provider value={state.operator}>{children}</AuthContext.Provider>;
}

function Centered({ children }: { children: ReactNode }) {
    return (
        <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 3 }}>{children}</Box>
    );
}
